import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from '../../../../app.service';
import { ApiRequestService } from '../../../../shared/api-request.service';
import { RBACService } from '../../../../shared/rbac.service';
import { UtilsService } from '../../../../shared/utils.service';
import { CustomDataSource } from '../../../../utils/custom-data-source';
import { CourseModuleSectionModel } from 'src/app/models/course-module-section.model';
import { CoursesModulesSectionsViewComponent } from '../course-modules-sections-view/courses-modules-sections-view.component';
import { CoursesModulesSectionsEditComponent } from '../course-modules-sections-edit/courses-modules-sections-edit.component';

@Component({
  selector: 'app-courses-modules-sections',
  templateUrl: './courses-modules-sections.component.html',
  styleUrls: ['./courses-modules-sections.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CoursesModulesSectionsComponent implements OnInit, AfterViewInit {
  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // Build displayedColumns dynamically: NOT as a static array.
  displayedColumns: string[] = [];
  dataSource = new SectionsDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private dialog: MatDialog,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access account inductions.'
      );
      this.router.navigate([
        '/courses',
        this.route.snapshot.params['course_id'],
        'modules'
      ]);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = ['id', 'name', 'status', 'date_created', 'actions'];
    if (this.canDelete) {
      this.displayedColumns.unshift('select');
    }

    // get the course and module ids
    this.dataSource.course_id = Number(this.route.snapshot.params['course_id']);
    this.dataSource.module_id = Number(this.route.snapshot.params['module_id']);

    if (this.dataSource.course_id && this.dataSource.module_id) {
      // 4. Only load data if user has view permission.
      if (this.canView) {
        this.dataSource.getData();
      }
    } else {
      // redirect back to modules because the ids are not valid
      this.router.navigate(['courses', this.dataSource.course_id, 'modules']);
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC columns based on their corresponding date fields.
      if (this.sort.active == 'date_created_UTC') {
        this.dataSource.sort_by = 'date_created';
      }

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onAdd() {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create induction sections.'
      );
      return;
    }
    this.openEditDialog(new CourseModuleSectionModel());
  }

  onView(section: CourseModuleSectionModel) {
    this.dialog
      .open(CoursesModulesSectionsViewComponent, {
        width: '900px',
        data: section
      })
      .afterClosed()
      .subscribe((success) => {
        if (!success) {
          return;
        }
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  onEdit(section: CourseModuleSectionModel) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit induction sections.'
      );
      return;
    }
    this.openEditDialog(section);
  }

  private openEditDialog(section: CourseModuleSectionModel) {
    this.dialog
      .open(CoursesModulesSectionsEditComponent, {
        width: '900px',
        data: {
          section: { ...section },
          course_id: this.dataSource.course_id,
          module_id: this.dataSource.module_id
        }
      })
      .afterClosed()
      .subscribe((success) => {
        if (!success) {
          return;
        }
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  onRemove(id: number) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete induction sections.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Sub-Section',
      'Are you sure you want to remove this sub-section?',
      () => {
        this.api
          .makeRequest(
            'delete',
            `v1/inductions/${this.dataSource.course_id}/modules/${
              this.dataSource.module_id
            }/sections/${id}`
          )
          .then((response: any) => {
            this.utils.showToast('The sub-section was removed.');
            this.dataSource.selection.deselect(id);
            this.dataSource.getData();
          });
      }
    );
  }

  onDeleteSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete induction sections.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Selected Sub-Sections',
      'Are you sure you want to remove the selected sub-sections?',
      () => {
        const selectedSections = this.dataSource.selection.selected.join(',');

        this.api
          .makeRequest(
            'delete',
            `v1/inductions/${this.dataSource.course_id}/modules/${
              this.dataSource.module_id
            }/sections/${selectedSections}`
          )
          .then((response: any) => {
            this.utils.showToast('The selected sub-sections was removed.');
            this.dataSource.selection.clear();
            this.dataSource.getData();
          });
      }
    );
  }
}

/**
 * the data source class should contain everything needed to get data for the feature.
 * this only applies to lists. forms will use direct calls to the api.
 */
export class SectionsDataSource extends CustomDataSource {
  course_id = 0;
  module_id = 0;

  sort_by = 'name';
  sort_order = 'asc';

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI(
      `inductions/${this.course_id}/modules/${this.module_id}/sections`,
      resetOffset
    );
  }
}
